import React, { useState, useEffect } from 'react';
import {
  Panel,
  PanelType,
  Stack,
  TextField,
  DefaultButton,
  PrimaryButton,
  Text,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { customPanelStyles } from '../../../theme';
import { modalContentStyles } from '../../../utils';
import { toastError, toastSuccess } from '../../../utils/toast';
import { normalizeBillitInvoice } from '../../billit-invoices/billitLinkAmounts';
import { refreshBillitPendingAssignedBanner } from '../pendingAssignedInvoices';
import { BillitInvoice } from '../../billit-invoices/types';
import { createPdfObjectUrlFromUblXml } from '../../billit-invoices/extractPdfFromUblXml';
import BillitPurchaseOrderLinks from '../../billit-invoices/components/BillitPurchaseOrderLinks';
import BillitInvoiceDetailLayout from '../../billit-invoices/components/BillitInvoiceDetailLayout';

interface Props {
  isOpen: boolean;
  dismissPanel: any;
  invoice?: BillitInvoice;
  refetch?: any;
}

const BillitInvoiceAssignedDetail = ({
  isOpen,
  dismissPanel,
  invoice,
  refetch,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [detailedInvoice, setDetailedInvoice] = useState<BillitInvoice | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [addingRemarks, setAddingRemarks] = useState(false);
  const [showSubjectToModal, setShowSubjectToModal] = useState(false);
  const [subjectToAction, setSubjectToAction] = useState<'approve' | 'reject' | null>(null);
  const [subjectToRemarks, setSubjectToRemarks] = useState('');
  const [subjectToApprove, setSubjectToApprove] = useState(false);
  const [subjectToReject, setSubjectToReject] = useState(false);
  const [attachmentRefreshKey, setAttachmentRefreshKey] = useState(0);
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      if (!invoice || !isOpen) return;

      try {
        setLoading(true);
        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setDetailedInvoice(normalizeBillitInvoice(data));
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to fetch invoice details';
        toastError(errorMessage);
        setDetailedInvoice(invoice);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
  }, [invoice, isOpen, getAccessTokenSilently]);

  useEffect(() => {
    const currentInvoiceData = detailedInvoice || invoice;
    if (!currentInvoiceData?.ubl_xml_content) {
      setPdfDataUrl(null);
      setPdfLoading(false);
      return;
    }

    setPdfLoading(true);
    setPdfError(null);

    const url = createPdfObjectUrlFromUblXml(currentInvoiceData.ubl_xml_content);
    setPdfDataUrl(url);
    setPdfLoading(false);
  }, [detailedInvoice?.ubl_xml_content, invoice?.ubl_xml_content]);

  useEffect(
    () => () => {
      if (pdfDataUrl) {
        URL.revokeObjectURL(pdfDataUrl);
      }
    },
    [pdfDataUrl],
  );

  // Reset modal state when closing
  useEffect(() => {
    if (!showRemarksModal) {
      setRemarks('');
    }
  }, [showRemarksModal]);

  const handleAddRemarks = async () => {
    if (!invoice || !remarks.trim()) return;

    try {
      setAddingRemarks(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/remarks`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            remarks: remarks.trim(),
            is_from_management: false, // This is called from employee view
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refetch invoice to get updated logs
      const invoiceResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (invoiceResponse.ok) {
        const data = await invoiceResponse.json();
        setDetailedInvoice(normalizeBillitInvoice(data));
      }

      setShowRemarksModal(false);
      toastSuccess('Opmerking toegevoegd');
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add remarks';
      toastError(errorMessage);
    } finally {
      setAddingRemarks(false);
    }
  };

  const handleSubjectToApprove = async () => {
    if (!invoice || !subjectToRemarks.trim()) return;

    try {
      setSubjectToApprove(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/subject-to/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            remarks: subjectToRemarks.trim(),
          }),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(normalizeBillitInvoice(data));
      setShowSubjectToModal(false);
      setSubjectToAction(null);
      setSubjectToRemarks('');
      toastSuccess('Factuur gemarkeerd als onderwerp goedkeuring');
      refreshBillitPendingAssignedBanner();
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to mark as subject-to-approve';
      toastError(errorMessage);
    } finally {
      setSubjectToApprove(false);
    }
  };

  const handleSubjectToReject = async () => {
    if (!invoice || !subjectToRemarks.trim()) return;

    try {
      setSubjectToReject(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}/billit/invoices/${invoice.billit_order_id}/subject-to/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            remarks: subjectToRemarks.trim(),
          }),
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDetailedInvoice(normalizeBillitInvoice(data));
      setShowSubjectToModal(false);
      setSubjectToAction(null);
      setSubjectToRemarks('');
      toastSuccess('Factuur gemarkeerd als onderwerp afwijzing');
      refreshBillitPendingAssignedBanner();
      if (refetch) {
        refetch();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to mark as subject-to-reject';
      toastError(errorMessage);
    } finally {
      setSubjectToReject(false);
    }
  };

  const parseJsonField = (jsonString: string | null | undefined) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  const getSupplierInfo = () => {
    const invoiceData = detailedInvoice || invoice;
    if (!invoiceData) return null;

    return parseJsonField(invoiceData.supplier_json);
  };

  const supplierInfo = getSupplierInfo();
  const invoiceData = detailedInvoice || invoice;

  if (!invoiceData) return null;

  const canSubjectToApproveReject = invoiceData.order_status === 'assigned';
  const logs = invoiceData.billit_invoice_logs || [];
  const sortedLogs = [...logs].sort(
    (a, b) => new Date(b.date_created).getTime() - new Date(a.date_created).getTime(),
  );
  const lastLog = sortedLogs[0];
  const employeeCommunicationAlert =
    lastLog?.is_from_management ? (
      <MessageBar
        messageBarType={MessageBarType.info}
        styles={{ root: { marginBottom: 12, borderLeft: '4px solid #ff8c00' } }}
      >
        Administratie heeft opmerkingen — reageer of markeer de factuur.
      </MessageBar>
    ) : null;

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel='Close'
      headerText={`Factuur ${
        invoiceData.order_number || invoiceData.billit_order_id
      }`}
      type={PanelType.custom}
      customWidth='min(1280px, 96vw)'
      styles={{
        root: {
          '.ms-Panel-main': {
            marginRight: 'auto',
            marginLeft: 'auto',
          },
        },
        main: { maxWidth: '96vw' },
        scrollableContent: {
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        },
        content: {
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          paddingBottom: '0px',
        },
      }}
    >
      {/* Remarks Panel */}
      {showRemarksModal && (
        <Panel
          isLightDismiss
          isOpen={showRemarksModal}
          onDismiss={() => setShowRemarksModal(false)}
          closeButtonAriaLabel='Close'
          headerText='Opmerking toevoegen'
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Opmerkingen'
                  value={remarks}
                  onChange={(_, newValue) => setRemarks(newValue || '')}
                  multiline
                  rows={6}
                  placeholder='Voer opmerkingen in'
                />
              </Stack>
            </div>

            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton onClick={() => setShowRemarksModal(false)}>
                  Annuleren
                </DefaultButton>
                <PrimaryButton
                  onClick={handleAddRemarks}
                  disabled={!remarks.trim() || addingRemarks}
                >
                  Toevoegen
                </PrimaryButton>
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      {/* Onderwerp goedkeuring / afwijzing - opmerking verplicht */}
      {showSubjectToModal && subjectToAction && (
        <Panel
          isLightDismiss
          isOpen={showSubjectToModal}
          onDismiss={() => {
            if (!subjectToApprove && !subjectToReject) {
              setShowSubjectToModal(false);
              setSubjectToAction(null);
              setSubjectToRemarks('');
            }
          }}
          closeButtonAriaLabel='Sluiten'
          headerText={subjectToAction === 'approve' ? 'Onderwerp goedkeuring' : 'Onderwerp afwijzing'}
          type={PanelType.custom}
          customWidth='600px'
          styles={customPanelStyles}
        >
          <div>
            <div className={modalContentStyles.header} />
            <div className={modalContentStyles.body}>
              <Stack tokens={{ childrenGap: 16 }}>
                <TextField
                  label='Opmerking (verplicht)'
                  value={subjectToRemarks}
                  onChange={(_, newValue) => setSubjectToRemarks(newValue || '')}
                  multiline
                  rows={5}
                  placeholder={subjectToAction === 'approve' ? 'Geef opmerking bij goedkeuring' : 'Geef reden van afwijzing'}
                  required
                />
              </Stack>
            </div>
            <div className={modalContentStyles.footer}>
              <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
                <DefaultButton
                  onClick={() => { setShowSubjectToModal(false); setSubjectToAction(null); setSubjectToRemarks(''); }}
                  disabled={subjectToApprove || subjectToReject}
                >
                  Annuleren
                </DefaultButton>
                {subjectToAction === 'approve' ? (
                  <PrimaryButton
                    onClick={handleSubjectToApprove}
                    disabled={!subjectToRemarks.trim() || subjectToApprove}
                    iconProps={{ iconName: 'Accept' }}
                  >
                    {subjectToApprove ? 'Bezig...' : 'Bevestigen'}
                  </PrimaryButton>
                ) : (
                  <DefaultButton
                    onClick={handleSubjectToReject}
                    disabled={!subjectToRemarks.trim() || subjectToReject}
                    styles={{
                      root: { borderColor: '#d13438', color: '#d13438' },
                      rootHovered: { borderColor: '#a4262c', color: '#a4262c' },
                    }}
                  >
                    {subjectToReject ? 'Bezig...' : 'Afwijzen'}
                  </DefaultButton>
                )}
              </Stack>
            </div>
          </div>
        </Panel>
      )}

      <div>
        <div className={modalContentStyles.header} />
        <div className={modalContentStyles.body}>
          <BillitInvoiceDetailLayout
            invoiceData={invoiceData}
            pdf={{ url: pdfDataUrl, loading: pdfLoading, error: pdfError }}
            supplierInfo={supplierInfo}
            attachmentRefreshKey={attachmentRefreshKey}
            onAddRemark={() => setShowRemarksModal(true)}
            communicationAlert={employeeCommunicationAlert}
            actionsBar={
              invoiceData.order_status !== 'approved' &&
              invoiceData.order_status !== 'rejected' &&
              canSubjectToApproveReject ? (
                <>
                  <DefaultButton
                    onClick={() => {
                      setSubjectToAction('reject');
                      setShowSubjectToModal(true);
                    }}
                    disabled={subjectToReject || subjectToApprove}
                    styles={{
                      root: { borderColor: '#d13438', color: '#d13438' },
                      rootHovered: { borderColor: '#a4262c', color: '#a4262c' },
                    }}
                  >
                    Onderwerp afwijzing
                  </DefaultButton>
                  <PrimaryButton
                    onClick={() => {
                      setSubjectToAction('approve');
                      setShowSubjectToModal(true);
                    }}
                    disabled={subjectToReject || subjectToApprove}
                    iconProps={{ iconName: 'Accept' }}
                  >
                    Onderwerp goedkeuring
                  </PrimaryButton>
                </>
              ) : undefined
            }
            purchaseOrderLinks={
              <BillitPurchaseOrderLinks
                invoice={invoice!}
                detailedInvoice={invoiceData}
                setDetailedInvoice={updated => {
                  setDetailedInvoice(updated);
                  setAttachmentRefreshKey(prev => prev + 1);
                }}
                onLinksChanged={refetch}
                invoicePdfUrl={pdfDataUrl}
                invoicePdfLoading={pdfLoading}
              />
            }
          />
        </div>

        <div className={modalContentStyles.footer}>
          <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 10 }}>
            <DefaultButton onClick={dismissPanel}>Sluiten</DefaultButton>
          </Stack>
        </div>
      </div>
    </Panel>
  );
};

export default BillitInvoiceAssignedDetail;
